import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { TransformedProduct } from "../../../../../types/productApiTypes";
import { useProductAddToCart } from "../../../../../../cart/hooks/useProductAddToCart";
import QuantityControl from "../../../../../../cart/components/common/QuantityControl";

interface GroupedProductItemProps {
  product: TransformedProduct;
}

const GroupedProductItem: React.FC<GroupedProductItemProps> = ({ product }) => {
  const { t } = useTranslation();

  // --- Sold Individually Logic ---
  const isSoldIndividually = product.soldIndividually;

  // Use minStock for the min value, defaulting to 1.
  const minQuantity = product.minStock || 1;
  const maxQuantity = isSoldIndividually
    ? 1
    : Math.min(product.maxStock || 100, 100);

  const [quantity, setQuantity] = useState(minQuantity);
  const { handleAddToCart, isAddingToCart } = useProductAddToCart();

  useEffect(() => {
    setQuantity(product.minStock || 1);
  }, [product.id, product.minStock]);

  const handleAddToCartWrapper = (e: React.MouseEvent<HTMLButtonElement>) => {
    const clampedQuantity = Math.max(
      minQuantity,
      Math.min(quantity, maxQuantity),
    );
    if (clampedQuantity !== quantity) {
      setQuantity(clampedQuantity);
    }
    handleAddToCart(e, { productId: product.id, quantity: clampedQuantity });
  };

  const image = product.mainImage;

  return (
    <li className="grouped-product-content__item">
      <div className="grouped-product-content__item-info">
        <a
          href={product.permalink}
          className="grouped-product-content__item-image-link"
        >
          {image ? (
            <img src={image.thumbnail} alt={image.alt || product.name} />
          ) : (
            <div className="placeholder-image"></div>
          )}
        </a>
        <div className="grouped-product-content__item-details">
          <a
            href={product.permalink}
            className="grouped-product-content__item-name"
          >
            {product.name}
          </a>
          <span className="grouped-product-content__item-price">
            {product.formattedPrice}
          </span>
        </div>
      </div>
      <div className="grouped-product-content__item-actions">
        {product.type === "simple" && product.stockStatus !== "outofstock" ? (
          <>
            <QuantityControl
              value={quantity}
              onChange={setQuantity}
              min={minQuantity}
              max={maxQuantity}
              disabled={isAddingToCart || isSoldIndividually}
              ariaLabel={t("productInfoDetails.quantityLabel", {
                productName: product.name,
              })}
              className="grouped-product-content__item-quantity"
            />
            <button
              className="grouped-product-content__item-add-btn"
              onClick={handleAddToCartWrapper}
              disabled={isAddingToCart}
            >
              {isAddingToCart
                ? t("productInfoDetails.addingToCart")
                : t("productInfoDetails.addToCartShort")}
            </button>
          </>
        ) : (
          <a
            href={product.permalink}
            className="grouped-product-content__item-view-btn"
          >
            {product.type === "variable"
              ? t("productInfoDetails.selectOptionsShort")
              : t("productInfoDetails.viewProduct")}
          </a>
        )}
      </div>
    </li>
  );
};

export default GroupedProductItem;
